/**
 * 魔棒（补齐 registry.ts 早先登记的占位工具 / DESIGN.md §5.1）。
 *
 * 单点泛洪：点一下按容差/连续性选出相近颜色区域，即 Photoshop 魔棒的语义。
 * 跟"快速选择"（quick-select.ts）共用同一个泛洪算法（engine/selection/
 * magic-wand.ts），区别只在交互——魔棒点一下就定，快速选择允许拖拽
 * 沿路径不断并入新区域。两个工具在工具栏里长按同一个槽位切换
 * （registry.ts 的 `TOOL_GROUPS`），跟真实 Photoshop 的 W 键分组一致。
 *
 * "消除锯齿"/"取样所有图层"这两个属性栏选项两个工具共用同一套实现，
 * 见 `magicWandSampleSource`（取样源）与下面 handlePointer 里的
 * `feather(mask, 1)`（消除锯齿，用轻微羽化近似，不是改 wasm 泛洪本身）。
 */
import { compositeDocument, flattenLayerToImage } from '../../app/composite';
import { magicWand, type SourceImage } from '../../engine/selection/magic-wand';
import { feather } from '../../engine/selection/ops';
import { applyCombine, combineFromModifiers, type SelectionCombine } from './marquee';
import type { AppStore } from '../../app/store';
import type { Document } from '../../document/types';
import type { ToolManager } from './registry';

/**
 * 取样源：默认（真实 PS 的默认值）只认当前图层——`compositeDocument` 会把
 * 所有可见图层都拿来判断颜色相近度，选出来的区域可能包含"当前图层里
 * 明明没画东西，只是恰好被下面图层的颜色骗过阈值"的地方。开了"取样所有
 * 图层"才用整份合成结果。
 */
export function magicWandSampleSource(
  store: AppStore,
  document: Document,
  sampleAllLayers: boolean,
): SourceImage {
  if (sampleAllLayers) return compositeDocument(document, store.tiles);
  const layerId = store.activeRasterLayerId();
  if (layerId === null) return compositeDocument(document, store.tiles); // 没有任何 raster 图层可取样，退回合成结果
  return flattenLayerToImage(store.tiles, layerId, document.size);
}

export interface MagicWandDeps {
  readonly store: AppStore;
  readonly tools: ToolManager;
  readonly requestRender: () => void;
}

export class MagicWandToolController {
  readonly #deps: MagicWandDeps;
  #busy = false;

  constructor(deps: MagicWandDeps) {
    this.#deps = deps;
  }

  /** down 即取样泛洪；泛洪是异步 wasm 调用，进行中忽略后续点击避免重入。 */
  handlePointer(
    phase: 'down' | 'move' | 'up' | 'double',
    point: { x: number; y: number },
    modifiers: { readonly shiftKey: boolean; readonly altKey: boolean },
  ): boolean {
    const { store, tools } = this.#deps;
    if (tools.active.id !== 'magicWand') return false;
    if (phase !== 'down') return true;
    const document = store.document;
    if (document === null || this.#busy) return true;

    const seedX = Math.floor(point.x);
    const seedY = Math.floor(point.y);
    if (seedX < 0 || seedY < 0 || seedX >= document.size.width || seedY >= document.size.height) {
      return true;
    }

    const tolerance = tools.getOption('magicWand', 'tolerance') as number;
    const contiguous = tools.getOption('magicWand', 'contiguous') as boolean;
    const antiAlias = tools.getOption('magicWand', 'antiAlias') as boolean;
    const sampleAllLayers = tools.getOption('magicWand', 'sampleAllLayers') as boolean;
    const buttonMode = tools.getOption('magicWand', 'combine') as SelectionCombine;
    const combine = combineFromModifiers(modifiers, buttonMode);
    const source = magicWandSampleSource(store, document, sampleAllLayers);

    this.#busy = true;
    void magicWand(source, seedX, seedY, { tolerance, contiguous })
      .then((mask) => {
        // 消除锯齿：wasm 泛洪本身是硬边缘（0/255），轻微羽化 1px 让边界出现
        // 中间值，视觉效果等价 PS 的抗锯齿，不用碰 Rust 重编 wasm
        const antiAliased = antiAlias ? feather(mask, 1) : mask;
        store.setSelection(applyCombine(store.selection, antiAliased, combine));
      })
      .catch((error: unknown) => {
        store.setHint(`魔棒选择失败：${error instanceof Error ? error.message : String(error)}`);
      })
      .finally(() => {
        this.#busy = false;
        this.#deps.requestRender();
      });
    return true;
  }
}
